import React, { useEffect, useRef, useState } from 'react';
import { Image, Loader2, Upload, X, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { createPortal } from 'react-dom';
import { type Project } from '../../hooks/queries/projectTypes';
import { useUploadProjectAvatar } from '../../hooks/queries/useProjects';
import { validateImageFile, getAllowedImageFormatsDisplay } from '../../utils/imageValidation';

interface ProjectEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onSubmit: (name: string, description: string, avatarUrl?: string) => Promise<void>;
  isSubmitting?: boolean;
}

export function ProjectEditModal({
  isOpen,
  onClose,
  project,
  onSubmit,
  isSubmitting = false,
}: ProjectEditModalProps) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [isUploading, setIsUploading] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(project.avatar_url);
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageValidationError, setImageValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setName(project.name);
      setDescription(project.description || '');
      setTempImageUrl(project.avatar_url);
      setTempImageFile(null);
      setImageError(false);
      setImageValidationError(null);
      setIsUploading(false);
    }
  }, [isOpen, project]);

  // Cleanup temp URLs
  useEffect(() => {
    return () => {
      if (tempImageUrl && tempImageUrl !== project.avatar_url) {
        URL.revokeObjectURL(tempImageUrl);
      }
    };
  }, [tempImageUrl, project.avatar_url]);

  const uploadProjectAvatar = useUploadProjectAvatar();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || isSubmitting) return;

    try {
      let avatarUrl;
      if (tempImageFile) {
        // Upload the image and get the URL
        const result = await uploadProjectAvatar.mutateAsync({
          projectId: project.id,
          file: tempImageFile,
        });
        avatarUrl = result.publicUrl;
      }

      await onSubmit(name.trim(), description.trim(), avatarUrl);
      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      setImageError(true);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type and size
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setImageValidationError(validation.error || 'Invalid file');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      setIsUploading(true);
      setImageError(false);
      setImageValidationError(null);

      // Cleanup previous temp URL
      if (tempImageUrl && tempImageUrl !== project.avatar_url) {
        URL.revokeObjectURL(tempImageUrl);
      }

      // Create new preview
      const previewUrl = URL.createObjectURL(file);
      setTempImageUrl(previewUrl);
      setTempImageFile(file);
    } catch (error) {
      console.error('Error handling project avatar:', error);
      setImageError(true);
      setTempImageUrl(project.avatar_url);
      setTempImageFile(null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-labelledby="modal-title"
        aria-modal="true"
        className="fixed inset-0 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      >
        <div className="bg-white w-full sm:w-[95vw] max-w-md sm:rounded-xl shadow-xl max-h-[100vh] sm:max-h-[95vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6">
            <h2 id="modal-title" className="text-xl sm:text-2xl font-bold text-gray-900">
              Edit Project
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              aria-label="Close modal"
              disabled={isSubmitting}
            >
              <X size={20} className="sm:hidden" />
              <X size={24} className="hidden sm:block" />
            </button>
          </div>

          {/* Form */}
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6"
          >
            {/* Project Image */}
            <div className="flex justify-center">
              <div
                className="relative w-24 h-24 sm:w-32 sm:h-32 rounded-2xl bg-gray-100 overflow-hidden cursor-pointer group shadow-sm hover:shadow-md transition-all duration-200"
                onClick={() => fileInputRef.current?.click()}
              >
                {isUploading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <Loader2 className="animate-spin text-blue-500" size={32} />
                  </div>
                ) : tempImageUrl && !imageError ? (
                  <img
                    src={tempImageUrl}
                    alt="Project avatar preview"
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                    onLoad={() => setImageError(false)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image size={32} className="text-gray-400 sm:hidden" />
                    <Image size={40} className="text-gray-400 hidden sm:block" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Upload size={24} className="text-white" />
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleFileChange}
                className="hidden"
                data-testid="avatar-input"
                aria-label="Upload project avatar"
                disabled={isSubmitting}
              />
            </div>
            {imageValidationError && (
              <div className="flex items-center justify-center gap-2 mt-2 text-red-500 text-xs sm:text-sm">
                <AlertCircle size={14} className="flex-shrink-0" />
                <span>{imageValidationError}</span>
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1 text-center">
              {getAllowedImageFormatsDisplay()} (max 10MB)
            </p>

            {/* Project Details */}
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block font-bold text-gray-700 mb-1">
                  Project Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full bg-gray-50 shadow rounded-lg px-3 py-2 sm:px-4 sm:py-2.5 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter project name"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="description" className="block font-bold text-gray-700 mt-10 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="w-full bg-gray-50 shadow rounded-lg px-3 py-2 sm:px-4 sm:py-2.5 outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Enter project description"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </form>

          {/* Action Buttons */}
          <div className=" p-4 sm:p-6 bg-white">
            <div className="flex gap-3 sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'flex-1 sm:flex-initial px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm sm:text-base',
                  'hover:bg-gray-200 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={() => formRef.current?.requestSubmit()}
                className={cn(
                  'flex-1 sm:flex-initial px-4 py-2 bg-blue-500 text-white rounded-lg text-sm sm:text-base',
                  'hover:bg-blue-600 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-2 font-medium'
                )}
                disabled={!name.trim() || isUploading || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <span>Save Changes</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
